import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';
import { serviceDetails, ServiceDetailLocale, ServiceDetail } from '@/mocks/serviceDetails';

// ──── SEO Helper: Body Location Mapping ────
function getBodyLocation(slug: string): string {
  const map: Record<string, string> = {
    'dental-implant': 'Kiefer',
    'zirconia-crown': 'Zähne',
    'ceramic-crown': 'Zähne',
    'invisible-braces': 'Zähne',
    'dental-cleaning': 'Zähne',
    'teeth-whitening': 'Zähne',
    'small-bone-graft': 'Kiefer',
    'large-bone-graft': 'Kiefer',
    'sinus-lift': 'Kieferhöhle',
    'pterygoid-implant': 'Oberkiefer',
    'zygomatic-implant': 'Oberkiefer / Jochbein',
    'elastic-denture': 'Zähne',
    'abdominoplasty': 'Bauch',
    'rhinoplasty': 'Nase',
    'rhinoplasty-septoplasty': 'Nase',
    'rhinoplasty-revision': 'Nase',
    'otoplasty': 'Ohren',
    'lower-blepharoplasty': 'Augen (Unterlid)',
    'upper-blepharoplasty': 'Augen (Oberlid)',
    'total-blepharoplasty': 'Augen (Ober- und Unterlid)',
    'breast-augmentation': 'Brust',
    'breast-reduction': 'Brust',
    'facelift': 'Gesicht',
    'facelift-mentor': 'Gesicht',
    'gynecomastia': 'Brust (männlich)',
    'abdominoplasty-liposuction': 'Bauch / Taille',
    'liposuction-bbl': 'Gesäß / Bauch / Taille',
    'liposuction-one-zone': 'variabel (1 Körperzone)',
    'liposuction-total-body': 'Ganzkörper',
    'face-lift': 'Gesicht / Hals',
    'arm-lift': 'Oberarme',
    'thigh-lift': 'Oberschenkel',
    'gastric-balloon': 'Magen',
  };
  return map[slug] || '';
}

// ──── SEO Helper: Build rich Meta Description ────
function buildMetaDescription(loc: ServiceDetailLocale, detail: ServiceDetail): string {
  const priceClause = detail.priceRange
    ? `Ab ${detail.priceRange.replace(/\s*[–-]\s*/g, '–')} EUR.`
    : '';
  const benefitClause = detail.category === 'dental'
    ? 'Bis 70% günstiger als in Deutschland, Österreich oder der Schweiz. Inkl. modernster 3D-Diagnostik.'
    : 'Bis 70% günstiger als in DE/AT/CH. Hotel, Flughafentransfer und persönliche Betreuung inklusive.';
  const clinicClause = 'Vertrauen Sie dem erfahrenen Team der Esteva Clinic Tirana – Ihrem Premium-Anbieter für ästhetische Medizin in Albanien.';

  const full = `${loc.heroTitle} in Tirana – ${loc.heroSubtitle} ${priceClause} ${benefitClause} ${clinicClause}`;
  // Enforce 150-160 char target; cut cleanly at last complete sentence within limit
  if (full.length <= 160) return full;
  const truncated = full.slice(0, 157);
  const lastDot = truncated.lastIndexOf('.');
  if (lastDot > 100) return truncated.slice(0, lastDot + 1);
  return truncated + '...';
}

// ──── SEO Helper: Build targeted Keywords ────
function buildKeywords(loc: ServiceDetailLocale, detail: ServiceDetail): string {
  const procedureKeywords = loc.heroTitle.split(' ').filter(w => w.length > 3).join(', ');
  const clinicBase = 'Esteva Clinic, Tirana, Albanien, ästhetische Medizin';
  const dentalKws = 'Zahnmedizin Tirana, Zahnersatz Ausland, Dental Tourismus Albanien, Zahnarzt Albanien';
  const surgeryKws = 'plastische Chirurgie Tirana, Schönheits-OP Ausland, plastische Chirurgie Albanien, ästhetische Chirurgie';

  const domainKws = detail.category === 'dental' ? dentalKws : surgeryKws;
  return `${procedureKeywords}, ${clinicBase}, ${domainKws}`;
}

// ──── SEO Helper: Parse price from range string ────
function parsePrice(priceRange: string): { price?: number; minPrice?: number; maxPrice?: number; priceCurrency: string } {
  const cleaned = priceRange.replace(/\./g, '').replace(/€/g, '').trim();
  if (cleaned.includes('–') || cleaned.includes('-')) {
    const parts = cleaned.split(/[–-]/).map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    if (parts.length >= 2) {
      return { minPrice: parts[0], maxPrice: parts[1], priceCurrency: 'EUR' };
    }
    if (parts.length === 1) {
      return { minPrice: parts[0], maxPrice: parts[0], priceCurrency: 'EUR' };
    }
  }
  const num = parseInt(cleaned, 10);
  if (!isNaN(num)) {
    return { price: num, priceCurrency: 'EUR' };
  }
  return { priceCurrency: 'EUR' };
}

// ──── SEO Helper: Build Schema.org MedicalProcedure JSON-LD ────
function buildMedicalProcedureSchema(loc: ServiceDetailLocale, detail: ServiceDetail, slug: string): object {
  const priceData = parsePrice(detail.priceRange);
  const bodyLocation = getBodyLocation(slug);
  const stepsText = loc.steps.map(s => `${s.title}: ${s.description}`).join(' | ');
  const howPerformedMax = 5000;
  const howPerformed = stepsText.length > howPerformedMax ? stepsText.slice(0, howPerformedMax - 3) + '...' : stepsText;

  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    priceCurrency: priceData.priceCurrency,
    eligibleRegion: { '@type': 'Country', 'name': 'AL' },
    availability: 'https://schema.org/InStock',
    description: `${loc.heroTitle} – ${detail.priceRange} EUR`,
  };
  if (priceData.price !== undefined) {
    offer.price = priceData.price;
  } else if (priceData.minPrice !== undefined && priceData.maxPrice !== undefined) {
    offer.minPrice = priceData.minPrice;
    offer.maxPrice = priceData.maxPrice;
  }

  // Build ImageObject array for both hero (1600x600) and intro (600x450) images
  const images = [
    {
      '@type': 'ImageObject',
      'url': loc.heroImage,
      'caption': `${loc.heroTitle} – Behandlung in der Esteva Clinic Tirana`,
      'width': 1600,
      'height': 600,
    },
    {
      '@type': 'ImageObject',
      'url': loc.introImage,
      'caption': `${loc.heroTitle} – Detailansicht & Ablauf – Esteva Clinic`,
      'width': 600,
      'height': 450,
    },
  ];

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    'name': loc.heroTitle,
    'description': loc.heroSubtitle,
    'image': images,
    'procedureType': detail.category === 'surgery' ? 'SurgicalProcedure' : 'NoninvasiveProcedure',
    'howPerformed': howPerformed,
    'offers': offer,
    'provider': {
      '@type': 'MedicalOrganization',
      'name': 'Esteva Clinic',
      'url': 'https://estevaclinic.com',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Tirana',
        'addressCountry': 'AL',
      },
    },
  };
  if (bodyLocation) {
    schema.bodyLocation = bodyLocation;
  }
  // Add followup info from step 4 (aftercare)
  if (loc.steps.length >= 4) {
    schema.followup = loc.steps[3].description;
  }
  // Add preparation info from step 1
  if (loc.steps.length >= 1) {
    schema.preparation = loc.steps[0].description;
  }

  return schema;
}

// ──── SEO Helper: Build Schema.org FAQPage JSON-LD ────
function buildFAQPageSchema(loc: ServiceDetailLocale): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': loc.faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
export default function ServiceDetailPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const detail = slug ? serviceDetails[slug] : undefined;

  // Get content in current language, fall back to German
  const lang = i18n.language?.split('-')[0] || 'de';
  const locale = detail
    ? (detail as unknown as Record<string, ServiceDetailLocale | undefined>)[lang]
    : undefined;
  const loc: ServiceDetailLocale | undefined = locale || detail?.de;
  const priceInfo = detail ? `${detail.priceRange} EUR` : '';

  // ──── Not Found ────
  if (!detail || !loc) {
    return (
      <>
        <Head
          title={`${t('svc.detail_not_found')} – Esteva Clinic`}
          metas={[{ name: 'robots', content: 'noindex' }]}
        />
        <main className="bg-background-50 min-h-screen">
        <Navbar />
        <section className="pt-32 pb-20 px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800 mt-4">{t('svc.detail_not_found')}</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label">
            <i className="ri-arrow-left-line"></i>{t('svc.detail_back_home')}
          </Link>
        </section>
        <Footer />
      </main>
      </>
    );
  }

  // ──── Build SEO data ────
  const metaDescription = buildMetaDescription(loc, detail);
  const keywords = buildKeywords(loc, detail);
  const medicalProcedureSchema = buildMedicalProcedureSchema(loc, detail, slug!);
  const faqPageSchema = buildFAQPageSchema(loc);
  const canonicalUrl = `https://estevaclinic.com/services/${slug}`;
  const isGermanPage = lang === 'de';

  const backLink = detail.category === 'surgery' ? '/pricing' : '/#services';

  return (
    <>
      <Head
        title={`${loc.heroTitle} – Esteva Clinic Tirana | ${detail.category === 'dental' ? 'Zahnmedizin' : 'Plastische Chirurgie'} Albanien`}
        metas={[
          { name: 'description', content: metaDescription },
          { name: 'keywords', content: keywords },
          { property: 'og:title', content: `${loc.heroTitle} – Esteva Clinic Tirana` },
          { property: 'og:description', content: metaDescription },
          { property: 'og:url', content: canonicalUrl },
          { property: 'og:type', content: 'website' },
          { property: 'og:image', content: loc.heroImage },
          { property: 'og:image:width', content: '1600' },
          { property: 'og:image:height', content: '600' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: `${loc.heroTitle} – Esteva Clinic` },
          { name: 'twitter:description', content: loc.heroSubtitle },
          { name: 'twitter:image', content: loc.heroImage },
          { name: 'last-modified', content: '2026-06-08' },
        ]}
        links={[{ rel: 'canonical', href: canonicalUrl }]}
        scripts={[
          { type: 'application/ld+json', children: JSON.stringify(medicalProcedureSchema) },
          { type: 'application/ld+json', children: JSON.stringify(faqPageSchema) },
        ]}
      />
      <main className="bg-background-50 min-h-screen">
      <Navbar />

      <section className="relative pt-20 md:pt-32 pb-10 md:pb-16 px-6 md:px-10 bg-primary-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src={loc.heroImage} alt={loc.heroTitle} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 to-primary-900/85"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <Link to={backLink} className="inline-flex items-center gap-2 text-white/50 hover:text-white/80 text-xs font-label tracking-wide transition-colors mb-4 md:mb-6">
            <i className="ri-arrow-left-line"></i>
            {detail.category === 'surgery' ? t('pricing.back_to_pricing') : t('services.tag')}
          </Link>
          <div className="flex flex-wrap items-baseline gap-3 md:gap-4">
            <h1 className="font-heading text-2xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
              {loc.heroTitle}
            </h1>
            {priceInfo && (
              <span className="text-lg md:text-2xl font-heading font-bold text-accent-400">
                {priceInfo}
              </span>
            )}
          </div>
          <p className="mt-3 md:mt-4 text-sm text-white/60 max-w-2xl font-body leading-relaxed">
            {loc.heroSubtitle}
          </p>
          {detail.stayInfo && (
            <p className="mt-2 text-xs text-white/40 font-label">
              <i className="ri-hotel-line mr-1"></i>
              {detail.stayInfo}
            </p>
          )}
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-16 items-center">
            <div className="flex-1">
              <h2 className="font-heading text-xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {loc.introHeading}
              </h2>
              <p className="mt-3 md:mt-5 text-sm text-foreground-500 leading-relaxed font-body">
                {loc.introText}
              </p>
            </div>
            <div className="flex-1 w-full">
              <img src={loc.introImage} alt={loc.heroTitle} className="w-full rounded-2xl object-cover h-48 sm:h-64 md:h-80" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 md:px-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-label">
              {t('service_detail.how_it_works')}
            </span>
            <h2 className="font-heading text-2xl md:text-5xl font-bold text-foreground-900 mt-3">
              {t('service_detail.process_title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {loc.steps.map((step, idx) => (
              <div key={idx} className="relative group">
                <div className="flex flex-row sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0 flex items-center justify-center rounded-2xl bg-primary-500 text-white group-hover:scale-110 transition-transform duration-300">
                    <i className={`${step.icon} text-lg md:text-2xl`}></i>
                  </div>
                  <div className="sm:mt-5">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900">{step.title}</h3>
                    <p className="mt-1.5 md:mt-2.5 text-sm text-foreground-500 leading-relaxed font-body">{step.description}</p>
                  </div>
                </div>
                {idx < loc.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)]">
                    <div className="border-t-2 border-dashed border-background-300"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-label">
              {t('service_detail.benefits')}
            </span>
            <h2 className="font-heading text-2xl md:text-5xl font-bold text-foreground-900 mt-3">
              {t('service_detail.benefits_title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6">
            {loc.benefits.map((benefit, idx) => (
              <div key={idx} className="flex gap-3 md:gap-5 p-4 md:p-6 rounded-2xl bg-background-50 border border-background-200/70 hover:border-secondary-300 transition-colors group">
                <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-200 transition-colors">
                  <i className={`${benefit.icon} text-base md:text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-sm md:text-lg font-semibold text-foreground-900">{benefit.title}</h3>
                  <p className="mt-1 text-xs md:text-sm text-foreground-500 leading-relaxed font-body">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 md:px-10 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-label">
              {t('service_detail.faq')}
            </span>
            <h2 className="font-heading text-2xl md:text-5xl font-bold text-foreground-900 mt-3">
              {t('service_detail.faq_title')}
            </h2>
          </div>
          <div className="space-y-3 md:space-y-4">
            {loc.faqs.map((faq, idx) => (
              <details key={idx} className="group rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between p-4 md:p-6 cursor-pointer list-none">
                  <span className="text-sm font-label font-semibold text-foreground-800 pr-4">{faq.question}</span>
                  <span className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 group-open:bg-primary-100 group-open:text-primary-600 group-open:rotate-45 transition-all duration-300">
                    <i className="ri-add-line text-sm"></i>
                  </span>
                </summary>
                <div className="px-4 md:px-6 pb-4 md:pb-6">
                  <p className="text-sm text-foreground-500 leading-relaxed font-body border-t border-background-200 pt-4">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 md:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="rounded-3xl bg-primary-900 p-6 md:p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-primary-900 to-secondary-950/40"></div>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-accent-500/5 blur-3xl"></div>
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-4xl font-bold text-white leading-tight">
                {t('service_detail.cta_heading')}
              </h2>
              <p className="mt-3 text-sm text-white/60 max-w-lg mx-auto font-body">
                {t('service_detail.cta_subtitle')}
              </p>
              <Link
                to="/booking"
                className="mt-6 md:mt-7 inline-flex items-center gap-2 px-6 md:px-10 py-3 md:py-4 bg-white text-primary-800 rounded-full text-xs md:text-sm tracking-[0.12em] uppercase font-label font-semibold hover:bg-white/95 hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap no-underline"
              >
                {loc.ctaText}
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  );
}